import { authApi } from './auth-api';
import type { AuthTokens } from '../types';

const REFRESH_BUFFER_MS = 60_000;

let tokens: AuthTokens | null = null;
let refreshTimeout: ReturnType<typeof setTimeout> | null = null;
let onTokenRefreshed: ((newTokens: AuthTokens) => void) | null = null;
let onRefreshFailed: (() => void) | null = null;

export function setTokenRefreshCallbacks(
  onRefreshed: (newTokens: AuthTokens) => void,
  onFailed: () => void,
): void {
  onTokenRefreshed = onRefreshed;
  onRefreshFailed = onFailed;
}

export function setTokens(newTokens: AuthTokens | null): void {
  tokens = newTokens;
  if (newTokens) {
    localStorage.setItem('accessToken', newTokens.accessToken);
  } else {
    localStorage.removeItem('accessToken');
  }
  scheduleRefresh();
}

export function getAccessToken(): string | null {
  return tokens?.accessToken ?? localStorage.getItem('accessToken');
}

export function getRefreshToken(): string | null {
  return tokens?.refreshToken ?? null;
}

export function clearTokens(): void {
  tokens = null;
  localStorage.removeItem('accessToken');
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
}

function scheduleRefresh(): void {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
  if (!tokens) return;
  const refreshInMs = (tokens.expiresIn || 900) * 1000 - REFRESH_BUFFER_MS;
  if (refreshInMs <= 0) {
    performRefresh();
    return;
  }
  refreshTimeout = setTimeout(performRefresh, refreshInMs);
}

async function performRefresh(): Promise<void> {
  if (!tokens?.refreshToken) {
    onRefreshFailed?.();
    return;
  }
  try {
    const newTokens = await authApi.refreshToken(tokens.refreshToken);
    tokens = newTokens;
    if (newTokens) {
      localStorage.setItem('accessToken', newTokens.accessToken);
    }
    scheduleRefresh();
    onTokenRefreshed?.(newTokens);
  } catch {
    clearTokens();
    onRefreshFailed?.();
  }
}
